import type { PropertySearchQuery } from '../helpers/useSearchQueries';
import type {
  SavedSearchFieldsFragment,
  SavedSearchPropertyType,
} from './__generated__/saved-searches.zeus';
import { SUPPORTED_PROPERTY_TYPES } from './mapping';

const formatPriceShort = (dollars: number): string => `$${Math.round(dollars / 1000)}k`;

const formatPriceRange = (min: number | null | undefined, max: number | null | undefined): string =>
  [min ? formatPriceShort(min) : '', max ? formatPriceShort(max) : ''].filter(Boolean).join('–');

const formatMarketSlug = (slug: string): string =>
  slug
    .split(/[-_]/)
    .filter((word) => word.length > 0)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

export function formatSavedSearchName(
  query: PropertySearchQuery,
  locationLabel?: string | null,
): string {
  const parts: string[] = [];
  if (query.bedrooms) {
    parts.push(`${query.bedrooms}+ bd`);
  }
  if (query.bathrooms) {
    parts.push(`${query.bathrooms}+ ba`);
  }
  if (query.minListPrice || query.maxListPrice) {
    parts.push(formatPriceRange(query.minListPrice, query.maxListPrice));
  }
  if (query.hasPool) {
    parts.push('pool');
  }
  const label = locationLabel || (query.market ? formatMarketSlug(query.market) : null);
  if (label) {
    parts.push(`in ${label}`);
  }
  return parts.length > 0 ? parts.join(', ') : 'My saved search';
}

const PROPERTY_TYPE_LABELS = {
  HOME: 'Houses',
  CONDO: 'Condos',
  TOWNHOME: 'Townhomes',
} satisfies Record<SavedSearchPropertyType, string>;

const formatSqft = (sqft: number): string => sqft.toLocaleString('en-US');

export function describeSavedSearch(saved: SavedSearchFieldsFragment): string {
  const parts: string[] = [];

  const zoneNames = saved.location.zones.map((zone) => zone.name).filter(Boolean);
  if (zoneNames.length > 0) {
    parts.push(zoneNames.join(', '));
  } else if (saved.location.vertices.length > 0) {
    parts.push('Custom map area');
  } else if (saved.location.market) {
    parts.push(formatMarketSlug(saved.location.market));
  }

  const f = saved.filters;
  if (f.minBedrooms) {
    parts.push(`${f.minBedrooms}+ bd`);
  }
  if (f.minBathrooms) {
    parts.push(`${f.minBathrooms}+ ba`);
  }
  if (f.minPriceDollars || f.maxPriceDollars) {
    parts.push(formatPriceRange(f.minPriceDollars, f.maxPriceDollars));
  }
  if (f.minSqft || f.maxSqft) {
    const min = f.minSqft ? formatSqft(f.minSqft) : '';
    const max = f.maxSqft ? formatSqft(f.maxSqft) : '';
    parts.push(`${[min, max].filter(Boolean).join('–')} sqft`);
  }
  const types = f.propertyTypes;
  if (types.length > 0 && types.length < SUPPORTED_PROPERTY_TYPES.length) {
    parts.push(types.map((type) => PROPERTY_TYPE_LABELS[type]).join('/'));
  }
  if (f.hasPoolOnly) {
    parts.push('Pool');
  }
  if (f.singleStoryOnly) {
    parts.push('Single story');
  }
  if (f.selfTourableOnly) {
    parts.push('Self-tour');
  }
  if (f.maxMonthlyHoaFeeDollars === 0) {
    parts.push('No HOA');
  }

  return parts.length > 0 ? parts.join(' · ') : 'All homes';
}
